import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import Group from 'components/utils/Group'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppSelector } from 'hooks/reduxHooks'
import useLuxPlusRenewalBonus, { getCurrentRenewalRewardStep } from 'luxPlus/hooks/useLuxPlusRenewalBonus'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import getLoyaltyNextTier from 'loyalty/selectors/getLoyaltyNextTier'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import { daysUntilDate } from 'lib/datetime/dateUtils'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LuxPlusRenewalBonusTimeline, { renewalBonusMessages, useRenewalRewardAmountText } from '../LandingPage/LuxPlusRenewalBonusTimeline'
import LuxPlusRetentionModalFooter from './LuxPlusRetentionModalFooter'

interface Props {
  onContinueToCancel: () => void
  onBack: () => void
}

const InnerCard = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.L};
  padding: ${rem(20)};
`

const BlueGiftIcon = styled(LineGiftIcon)`
  color: ${(props) => props.theme.palette.product.luxPlus.foreground};
`

const LadderBox = styled.div`
  background-color: ${(props) => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${(props) => props.theme.borderRadius.L};
  padding: ${rem(20)};
`

const messages = defineMessages({
  titleImminent: {
    id: '7_8vWZKq4NCc5fd-2kJd',
    defaultMessage: 'Your renewal bonus is almost here',
    description: 'Title when the renewal (and its bonus) is imminent',
  },
  titleDaysAway: {
    id: '3eZKmf77zsQz2B4Xpbgx',
    defaultMessage: "You're just {days} days away from your renewal bonus",
    description: 'Retention modal title shown when the renewal bonus is within 30 days away',
  },
  titleGettingCloser: {
    id: 'B-5i2iTckIV2Be5GCAxk',
    defaultMessage: 'Your next renewal bonus is getting closer',
    description: 'Retention modal title shown when the renewal bonus is 31 to 90 days away',
  },
  titleNext: {
    id: 'CfWJqP_odasbLeNZ9lo1',
    defaultMessage: 'Your next renewal bonus',
    description: 'Title when the renewal bonus is more than 90 days away',
  },
  subtitle: {
    id: '70jw2AdwCjrtPKbcnRdz',
    defaultMessage: 'Remain a {membershipName} member and receive your renewal bonus on <b>{date}</b>.',
    description: 'Subtitle telling the member when they receive their renewal bonus',
  },
  eyebrow: {
    id: 'Yy6aLJDN5OC0A-owwaAa',
    defaultMessage: 'Your year {year} renewal bonus',
    description: 'Eyebrow label above the renewal bonus amount in the retention modal reward card',
  },
  nextTierBlurb: {
    id: '_Pr7fu6xFXICyuSZCN_d',
    defaultMessage: 'Bringing you closer to {nextTier} status, unlocking free upgrades, airport lounge passes & more.',
    description: 'Describes the benefit of reaching the next loyalty tier',
  },
  almostHere: {
    id: 'dpMa0cyLmTkgsNuKlcOb',
    defaultMessage: "You're almost here",
    description: 'Caption under the current renewal step in the retention modal reward timeline',
  },
})

function LuxPlusManageAccountModalRenewalBonusRetention({ onContinueToCancel, onBack }: Props) {
  const intl = useIntl()

  const { steps, isLoading } = useLuxPlusRenewalBonus()
  const hideStatusCredits = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const isInfinite = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  // Top tiers have no next tier to progress to: platinum yields no next tier, and infinite sits
  // outside the public tier ladder entirely.
  const nextTier = useAppSelector(getLoyaltyNextTier)
  const showNextTierBlurb = !!nextTier && !isInfinite
  const renewalDate = useAppSelector(getLuxPlusSubscriptionRenewalDate)
  const formatRewardAmount = useRenewalRewardAmountText()

  // Highlight the 1st renewal as current when there's no active progression yet.
  const displaySteps = steps.some((step) => step.state === 'current')
    ? steps
    : steps.map((step, index) => (index === 0 ? { ...step, state: 'current' as const } : step))
  const nextReward = getCurrentRenewalRewardStep(displaySteps)
  const renewalYear = nextReward?.year ?? 1
  const rewardAmountText = formatRewardAmount(nextReward, { hideStatusCredits })

  const daysUntilRenewal = renewalDate ? daysUntilDate(new Date(renewalDate)) : undefined
  const title = daysUntilRenewal === undefined
    ? intl.formatMessage(messages.titleNext)
    : daysUntilRenewal <= 1
      ? intl.formatMessage(messages.titleImminent)
      : daysUntilRenewal <= 30
        ? intl.formatMessage(messages.titleDaysAway, { days: daysUntilRenewal })
        : daysUntilRenewal <= 90
          ? intl.formatMessage(messages.titleGettingCloser)
          : intl.formatMessage(messages.titleNext)

  return <>
    <ModalHeader title={title} onBackButtonClick={onBack} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={20}>
          {renewalDate && <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              {...messages.subtitle}
              values={{
                membershipName: LUXURY_PLUS.PROGRAM_NAME,
                date: <FormatDateTime value={renewalDate} format="dmy-casual" />,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </BodyText>}
          <Group direction="vertical" gap={12}>
            <InnerCard>
              <Group direction="vertical" gap={12}>
                <Group direction="vertical" gap={4}>
                  <Group direction="horizontal" gap={8} verticalAlign="center">
                    <BlueGiftIcon size="XS" />
                    <BodyTextBlock variant="medium" weight="bold" colour="lux-plus">
                      {intl.formatMessage(messages.eyebrow, { year: renewalYear })}
                    </BodyTextBlock>
                  </Group>
                  <HeadingTextBlock variant="heading3">{rewardAmountText}</HeadingTextBlock>
                  {showNextTierBlurb && <BodyText variant="medium" colour="light-foreground-alt">
                    {intl.formatMessage(messages.nextTierBlurb, { nextTier: getLoyaltyTierDisplayName(nextTier) })}
                  </BodyText>}
                </Group>
                {/* Infinite members earn points only — no Status Credits or tier ladder to show. */}
                {!isInfinite && <LadderBox>
                  <LuxPlusRenewalBonusTimeline
                    steps={displaySteps}
                    isLoading={isLoading}
                    currentStepCaption={intl.formatMessage(messages.almostHere)}
                    desktopAmountsMaxWidthRem={128}
                  />
                </LadderBox>}
              </Group>
            </InnerCard>
            <CaptionTextBlock variant="medium" colour="light-foreground-alt">
              {intl.formatMessage(renewalBonusMessages.footnote, { membershipName: LUXURY_PLUS.PROGRAM_NAME })}
            </CaptionTextBlock>
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <LuxPlusRetentionModalFooter onContinueToCancel={onContinueToCancel} />
  </>
}

export default LuxPlusManageAccountModalRenewalBonusRetention
